// Support context: which screen the customer is on and the records in view.
// Screens call setSupportContext (class components, in componentDidMount) or
// useSupportContext (function components). The composer reads it when a
// message is sent; the server adds the signed-in account, tracking and history.
import { useEffect } from 'react';

const ID_FIELDS = [
  'orderIds',
  'restaurantId',
  'menuId',
  'productIds',
  'companyId',
  'routeId',
  'printJobId',
  'invoiceId',
];
const LIST_FIELDS = ['orderIds', 'productIds'];
const MAX_LIST = 20;

let current = null;
const listeners = new Set();

function currentPath() {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

function present(value) {
  return value !== undefined && value !== null && value !== '';
}

export function cleanIds(ids) {
  const cleaned = {};
  if (!ids || typeof ids !== 'object') {
    return cleaned;
  }
  ID_FIELDS.forEach(field => {
    const value = ids[field];
    if (LIST_FIELDS.indexOf(field) >= 0) {
      const list = Array.isArray(value)
        ? value.filter(present).slice(0, MAX_LIST)
        : [];
      if (list.length) {
        cleaned[field] = list;
      }
    } else if (present(value)) {
      cleaned[field] = value;
    }
  });
  return cleaned;
}

/**
 * @param {string} screenId e.g. 'checkout.cart'
 * @param {{orderIds?: Array<string|number>, restaurantId?: string|number,
 *   menuId?: string|number, productIds?: Array<string|number>,
 *   companyId?: string|number, routeId?: string|number, printJobId?: string,
 *   invoiceId?: string}} [ids]
 */
export function setSupportContext(screenId, ids) {
  current = {
    screenId: screenId || null,
    ids: cleanIds(ids),
    path: currentPath(),
  };
  notify();
}

/**
 * For a drawer or modal over the page (the basket, the payment result): sets its context
 * and returns a function that puts the page's context back when it closes.
 */
export function overlaySupportContext(screenId, ids) {
  const previous = current;
  const overlay = {
    screenId: screenId || null,
    ids: cleanIds(ids),
    path: currentPath(),
  };
  current = overlay;
  notify();
  return () => {
    if (current === overlay) {
      current = previous;
      notify();
    }
  };
}

// Listeners run after the current render: screens may set context while rendering.
function notify() {
  setTimeout(() => listeners.forEach(listener => listener()), 0);
}

// Hook form for function components: sets the context while the screen is shown.
export function useSupportContext(screenId, ids) {
  const key = JSON.stringify([screenId || null, cleanIds(ids)]);
  useEffect(() => {
    setSupportContext(screenId, ids);
  }, [key]);
}

// A context set on another page is stale: screens without a hook report only their path.
export function getSupportContext() {
  const path = currentPath();
  if (current && current.path === path) {
    return { screenId: current.screenId, ids: current.ids, path };
  }
  return { screenId: null, ids: {}, path };
}

export function subscribeSupportContext(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}



// WEBPACK FOOTER //
// ./src/support/context.js